import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Building2, User, Phone, Mail, MapPin, Lock, Camera, Loader2, AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function FirstLoginSetup() {
  const navigate = useNavigate();
  const { user, token, updateUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  
  const [formData, setFormData] = useState({
    // Company Information
    company_name: user?.company_name || '',
    company_code: user?.company_code || '',
    vat_code: user?.vat_code || '',
    address: user?.address || '',
    // Representative Information
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    // Password Change
    current_password: '',
    new_password: '',
    confirm_password: '',
    // Optional
    avatar_base64: user?.avatar_base64 || ''
  });

  const [fieldErrors, setFieldErrors] = useState({});

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    // Company fields
    if (!formData.company_name.trim()) errors.company_name = 'Company Name is required';
    if (!formData.company_code.trim()) errors.company_code = 'Company Code is required';
    // VAT Code is optional - removed from required validation
    if (!formData.address.trim()) errors.address = 'Address is required';
    
    // Representative fields
    if (!formData.name.trim()) errors.name = 'Representative Name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    if (!formData.phone.trim()) errors.phone = 'Phone is required';
    
    // Password fields
    if (!formData.current_password) errors.current_password = 'Current password is required';
    if (!formData.new_password) errors.new_password = 'New password is required';
    if (formData.new_password && formData.new_password.length < 6) {
      errors.new_password = 'Password must be at least 6 characters';
    }
    if (!formData.confirm_password) errors.confirm_password = 'Confirm password is required';
    if (formData.new_password && formData.confirm_password && formData.new_password !== formData.confirm_password) {
      errors.confirm_password = 'Passwords do not match';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isFormValid = () => {
    return (
      formData.company_name.trim() &&
      formData.company_code.trim() &&
      // VAT Code is optional
      formData.address.trim() &&
      formData.name.trim() &&
      formData.email.trim() &&
      formData.phone.trim() &&
      formData.current_password &&
      formData.new_password &&
      formData.new_password.length >= 6 &&
      formData.confirm_password &&
      formData.new_password === formData.confirm_password
    );
  };

  const handleAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 500 * 1024) {
        setError('Avatar image must be less than 500KB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, avatar_base64: reader.result }));
        setError('');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!validateForm()) {
      setError('Please fill in all required fields');
      return;
    }
    
    setSaving(true);
    try {
      // Prepare the payload - exclude empty avatar to avoid body size issues
      const payload = {
        company_name: formData.company_name,
        company_code: formData.company_code,
        vat_code: formData.vat_code || '',  // Optional field
        address: formData.address,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        current_password: formData.current_password,
        new_password: formData.new_password,
        confirm_password: formData.confirm_password
      };
      
      // Only include avatar if it has a value
      if (formData.avatar_base64) {
        payload.avatar_base64 = formData.avatar_base64;
      }
      
      const response = await fetch(`${API}/api/profile/first-login-setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      // Clone the response before reading to avoid "body is disturbed" error
      const responseClone = response.clone();
      
      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        // If JSON parsing fails, try to get text from clone
        const text = await responseClone.text();
        throw new Error(text || 'Failed to parse server response');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Setup failed');
      }
      
      // Update user in context
      updateUser(data);
      setSuccess('Setup completed successfully! Redirecting...');
      
      // Redirect to partner dashboard after a brief delay
      setTimeout(() => {
        navigate('/partner', { replace: true });
      }, 1500);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-['Chivo'] text-3xl text-white mb-2">Welcome to Unique Protection</h1>
          <p className="text-zinc-400">Complete your account setup to get started</p>
        </div>

        {/* Main Form Card */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 md:p-8">
          <form onSubmit={handleSubmit} className="space-y-8">
            
            {/* Error/Success Messages */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-600/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}
            {success && (
              <div className="flex items-center gap-2 p-3 bg-green-600/10 border border-green-500/20 rounded-lg text-green-400 text-sm">
                <CheckCircle className="w-4 h-4 flex-shrink-0" />
                {success}
              </div>
            )}

            {/* Avatar Upload (Optional) */}
            <div className="flex flex-col items-center gap-4">
              <div className="relative">
                {formData.avatar_base64 ? (
                  <img src={formData.avatar_base64} alt="Avatar" className="w-24 h-24 rounded-full object-cover border-2 border-zinc-700" />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-zinc-800 border-2 border-zinc-700 flex items-center justify-center">
                    <User className="w-10 h-10 text-zinc-500" />
                  </div>
                )}
                <label className="absolute bottom-0 right-0 w-8 h-8 bg-red-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-red-700 transition-colors">
                  <Camera className="w-4 h-4 text-white" />
                  <input type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" />
                </label>
              </div>
              <span className="text-xs text-zinc-500">Upload avatar (optional, max 500KB)</span>
            </div>

            {/* Company Information Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-zinc-800">
                <Building2 className="w-5 h-5 text-red-500" />
                <h2 className="font-['Chivo'] text-lg text-white">Company Information</h2>
                <span className="text-xs text-red-400 ml-auto">* All required</span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="company_name" className="text-zinc-300 text-sm">Company Name *</Label>
                  <Input
                    id="company_name"
                    value={formData.company_name}
                    onChange={handleChange('company_name')}
                    placeholder="Your Company Ltd."
                    className={`bg-zinc-900 border-zinc-700 text-white ${fieldErrors.company_name ? 'border-red-500' : ''}`}
                  />
                  {fieldErrors.company_name && <p className="text-xs text-red-400">{fieldErrors.company_name}</p>}
                </div>
                
                <div className="space-y-1.5">
                  <Label htmlFor="company_code" className="text-zinc-300 text-sm">Company Code *</Label>
                  <Input
                    id="company_code"
                    value={formData.company_code}
                    onChange={handleChange('company_code')}
                    placeholder="123456789"
                    className={`bg-zinc-900 border-zinc-700 text-white ${fieldErrors.company_code ? 'border-red-500' : ''}`}
                  />
                  {fieldErrors.company_code && <p className="text-xs text-red-400">{fieldErrors.company_code}</p>}
                </div>
                
                <div className="space-y-1.5">
                  <Label htmlFor="vat_code" className="text-zinc-300 text-sm">VAT Code <span className="text-zinc-500">(optional)</span></Label>
                  <Input
                    id="vat_code"
                    value={formData.vat_code}
                    onChange={handleChange('vat_code')}
                    placeholder="LT123456789"
                    className="bg-zinc-900 border-zinc-700 text-white"
                  />
                </div>
                
                <div className="space-y-1.5">
                  <Label htmlFor="address" className="text-zinc-300 text-sm">Address *</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={handleChange('address')}
                    placeholder="123 Main St, City"
                    className={`bg-zinc-900 border-zinc-700 text-white ${fieldErrors.address ? 'border-red-500' : ''}`}
                  />
                  {fieldErrors.address && <p className="text-xs text-red-400">{fieldErrors.address}</p>}
                </div>
              </div>
            </div>

            {/* Representative Information Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-zinc-800">
                <User className="w-5 h-5 text-red-500" />
                <h2 className="font-['Chivo'] text-lg text-white">Representative Information</h2>
                <span className="text-xs text-red-400 ml-auto">* All required</span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="name" className="text-zinc-300 text-sm">Representative Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={handleChange('name')}
                    placeholder="John Doe"
                    className={`bg-zinc-900 border-zinc-700 text-white ${fieldErrors.name ? 'border-red-500' : ''}`}
                  />
                  {fieldErrors.name && <p className="text-xs text-red-400">{fieldErrors.name}</p>}
                </div>
                
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-zinc-300 text-sm">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange('email')}
                      placeholder="you@company.com"
                      className={`pl-10 bg-zinc-900 border-zinc-700 text-white ${fieldErrors.email ? 'border-red-500' : ''}`}
                    />
                  </div>
                  {fieldErrors.email && <p className="text-xs text-red-400">{fieldErrors.email}</p>}
                </div>
                
                <div className="space-y-1.5 md:col-span-2">
                  <Label htmlFor="phone" className="text-zinc-300 text-sm">Phone *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={handleChange('phone')}
                      placeholder="+370 600 00000"
                      className={`pl-10 bg-zinc-900 border-zinc-700 text-white ${fieldErrors.phone ? 'border-red-500' : ''}`}
                    />
                  </div>
                  {fieldErrors.phone && <p className="text-xs text-red-400">{fieldErrors.phone}</p>}
                </div>
              </div>
            </div>

            {/* Password Change Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-zinc-800">
                <Lock className="w-5 h-5 text-red-500" />
                <h2 className="font-['Chivo'] text-lg text-white">Change Password</h2>
                <span className="text-xs text-red-400 ml-auto">* Required on first login</span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="current_password" className="text-zinc-300 text-sm">Current Password *</Label>
                  <div className="relative">
                    <Input
                      id="current_password"
                      type={showPasswords.current ? "text" : "password"}
                      value={formData.current_password}
                      onChange={handleChange('current_password')}
                      placeholder="Temp password"
                      className={`pr-10 bg-zinc-900 border-zinc-700 text-white ${fieldErrors.current_password ? 'border-red-500' : ''}`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(p => ({ ...p, current: !p.current }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                      tabIndex={-1}
                    >
                      {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {fieldErrors.current_password && <p className="text-xs text-red-400">{fieldErrors.current_password}</p>}
                </div>
                
                <div className="space-y-1.5">
                  <Label htmlFor="new_password" className="text-zinc-300 text-sm">New Password *</Label>
                  <div className="relative">
                    <Input
                      id="new_password"
                      type={showPasswords.new ? "text" : "password"}
                      value={formData.new_password}
                      onChange={handleChange('new_password')}
                      placeholder="Min 6 characters"
                      className={`pr-10 bg-zinc-900 border-zinc-700 text-white ${fieldErrors.new_password ? 'border-red-500' : ''}`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(p => ({ ...p, new: !p.new }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                      tabIndex={-1}
                    >
                      {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {fieldErrors.new_password && <p className="text-xs text-red-400">{fieldErrors.new_password}</p>}
                </div>
                
                <div className="space-y-1.5">
                  <Label htmlFor="confirm_password" className="text-zinc-300 text-sm">Confirm Password *</Label>
                  <div className="relative">
                    <Input
                      id="confirm_password"
                      type={showPasswords.confirm ? "text" : "password"}
                      value={formData.confirm_password}
                      onChange={handleChange('confirm_password')}
                      placeholder="Repeat password"
                      className={`pr-10 bg-zinc-900 border-zinc-700 text-white ${fieldErrors.confirm_password ? 'border-red-500' : ''}`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(p => ({ ...p, confirm: !p.confirm }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                      tabIndex={-1}
                    >
                      {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {fieldErrors.confirm_password && <p className="text-xs text-red-400">{fieldErrors.confirm_password}</p>}
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <Button
                type="submit"
                disabled={saving || !isFormValid()}
                className="w-full h-12 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Complete Setup & Continue'
                )}
              </Button>
              
              {!isFormValid() && (
                <p className="text-xs text-zinc-500 text-center mt-2">
                  Fill in all required fields to enable the button
                </p>
              )}
            </div>
          </form>
        </div>

        {/* Footer Note */}
        <p className="text-xs text-zinc-600 text-center mt-4">
          You must complete this setup to access your dashboard
        </p>
      </div>
    </div>
  );
}
